<script lang="ts">
import { defineComponent, PropType, inject, ref, computed } from 'vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import NewsletterSetupForRegistration from '@/components/user/registration/NewsletterSetupForRegistration.vue'
import PaginationRegistrationStep from '@/components/user/registration/PaginationRegistrationStep.vue'
import PrivacyPolicyAgreement from '@/components/solutions/PrivacyPolicyAgreement.vue'
import RecaptchaDefault from '@/components/recaptcha/RecaptchaDefault.vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import validationOperator from '@/models/validationOperator'

export default defineComponent({
  name: 'InputStep4UserRegistrationForShowContents',

  components: {
    ButtonSubmit,
    NewsletterSetupForRegistration,
    PaginationRegistrationStep,
    PrivacyPolicyAgreement,
    RecaptchaDefault,
    ValidationObserver,
  },

  props: {
    namespace: {
      type: String,
      default: '',
    },

    /**
     * reCAPTCHA を読み込んでよいか
     *
     * @description false の間はウィジェットを描画せず、スクリプトも読み込まない。
     *   未指定時は従来どおり即時に読み込む（送信できなくなる事故を避けるため）。
     */
    isRecaptchaLoadable: {
      type: Boolean,
      default: true,
    },

    inputOptionalNewsletterInfo: {
      type: Object as PropType<OptionalNewsletterSettings>,
      default: () => ({})
    },

    submitButtonCaption: {
      type: String,
      default: '',
    },

    /** 情報提供先の掲載企業名（submit 直上の PrivacyPolicyAgreement に渡す） */
    agreementCompanyName: {
      type: String,
      default: '',
    },
  },

  emits: [
    'update:mailStopFlag',
    'update:mailNewsStopFlag',
    'update:mailExpoStopFlag',
    'update:mailAiStopFlag',
    'click:submit',
    'verified:recaptcha',
    'expired:recaptcha',
  ],

  setup(props, { emit }) {
    const { validationObserver, validate } = validationOperator()

    const MAIL_STOP_CATEGORIES = inject<Record<string, Record<string, string>>>('MAIL_STOP_CATEGORIES')!
    const isVerified = ref(false)
    const isSubmitHover = ref(false)

    /**
     * ReCAPTCHA エラー表示有無
     *
     * @description ReCAPTCHA が未認証状態でsubmit ボタンにマウスオーバーした場合は注意表示
     */
    const isShowRecaptchaError = computed(() => {
      return isSubmitHover.value && !isVerified.value
    })

    /**
     * reCAPTCHA 認証処理
     * @param response reCAPTCHA から返却されるトークン
     */
    const onVerified = (response: string) => {
      isVerified.value = !!response
      emit('verified:recaptcha', String(response))
    }

    /**
     * reCAPTCHA 有効期限切れ処理
     */
    const onExpired = () => {
      isVerified.value = false
      emit('expired:recaptcha')
    }

    const onMouseOverSubmit = () => {
      isSubmitHover.value = true
    }

    const onMouseOutSubmit = () => {
      isSubmitHover.value = false
    }

    const submit = () => {
      emit('click:submit')
    }

    return {
      // constants
      MAIL_STOP_CATEGORIES,

      // data
      validationObserver,
      isVerified,
      isSubmitHover,

      // computed
      isShowRecaptchaError,

      // methods
      validate,
      onVerified,
      onExpired,
      onMouseOverSubmit,
      onMouseOutSubmit,
      submit,
    }
  }
})
</script>

<template>
  <ValidationObserver
    ref="validationObserver"
    :namespace="namespace"
    @valid="submit">
    <div class="o-heading__section">
      <h2 class="o-heading__section--item">
        受け取るニュースレターを選択してください
      </h2>
    <!-- /.o-heading__section --></div>

    <PaginationRegistrationStep :current-step="4" />

    <NewsletterSetupForRegistration
      :namespace="namespace"
      :input-optional-newsletter-info="inputOptionalNewsletterInfo"
      @update:mail-stop-flag="$emit('update:mailStopFlag', $event)"
      @update:mail-news-stop-flag="$emit('update:mailNewsStopFlag', $event)"
      @update:mail-expo-stop-flag="$emit('update:mailExpoStopFlag', $event)"
      @update:mail-ai-stop-flag="$emit('update:mailAiStopFlag', $event)" />

    <RecaptchaDefault
      v-if="isRecaptchaLoadable"
      :is-error="isShowRecaptchaError"
      @verified="onVerified"
      @expired="onExpired" />

    <PrivacyPolicyAgreement :company-name="agreementCompanyName" />

    <ButtonSubmit
      :is-link="false"
      :has-shadow="false"
      :disabled="!isVerified"
      :caption="submitButtonCaption"
      prevent
      stop
      @click:submit="validate"
      @mouseover="onMouseOverSubmit"
      @mouseout="onMouseOutSubmit">
      無料登録する
    </ButtonSubmit>
  </ValidationObserver>
</template>

<style lang="sass" scoped>
// layout
.l-unit__defaultNewsletter
  margin-top: 1.7em
  padding: 24px
  background-color: rgba($bg-x-light-gray, 1)

  &:first-child
    margin-top: 0

  +breakpoint
    padding: 16px

// object
.o-heading__section
  & &--item
    font:
      size: $font-normal
      weight: bold
    text-align: center

  + .p
    margin-top: 0.5em
</style>
